import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  api,
  type LineLinkDisconnectData,
  type LineLinkStatusData,
  type LineOAuthAuthorizeData,
} from "../lib/api";
import { resolveLineOAuthPopupCompleteUrl } from "../lib/config";
import { AppError, logError } from "../lib/errors";
import {
  clearLineOAuthPopupSnapshot,
  isLineOAuthPopupMessage,
  readLineOAuthPopupSnapshot,
  writeLineOAuthContextSnapshot,
} from "../lib/lineOAuthPopup";
import {
  getLineLinkStatusDescription,
  getLineLinkStatusLabel,
  readLineLinkSnapshot,
  resolveLineLinkSnapshotFromApiStatus,
  type LineLinkSnapshot,
  writeLineLinkSnapshot,
} from "../lib/lineLink";
import { getLineStatusBadgeClass, UI_TEXT_TONE } from "../lib/uiTone";

type LineLinkCardProps = {
  returnTo: string;
  returnState?: unknown;
};

const LINE_OAUTH_POPUP_NAME = "wizmate-line-oauth";
const LINE_OAUTH_STORAGE_KEY = "wizmate.line-oauth-complete";

function isMobileUserAgent() {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

function formatLinkedAt(value?: string) {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("ja-JP", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function openCenteredPopup(url: string) {
  const width = 500;
  const height = 600;
  const dualScreenLeft = window.screenLeft ?? window.screenX ?? 0;
  const dualScreenTop = window.screenTop ?? window.screenY ?? 0;
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || window.screen.width;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || window.screen.height;
  const left = Math.max(0, Math.round(viewportWidth / 2 - width / 2 + dualScreenLeft));
  const top = Math.max(0, Math.round(viewportHeight / 2 - height / 2 + dualScreenTop));

  return window.open(
    url,
    LINE_OAUTH_POPUP_NAME,
    `popup=yes,width=${width},height=${height},left=${left},top=${top}`,
  );
}

function toGenericErrorMessage() {
  return "LINE連携の処理に失敗しました。時間をおいてもう一度お試しください。";
}

function getConnectErrorMessage(error: unknown) {
  if (error instanceof AppError && error.code === "INVALID_RESPONSE") {
    return "LINEの認証画面を開けませんでした。ポップアップ設定をご確認のうえ、もう一度お試しください。";
  }

  return toGenericErrorMessage();
}

function getDisconnectErrorMessage(error: unknown) {
  if (error instanceof AppError && error.code === "NOT_FOUND") {
    return "LINE連携の状態が見つかりませんでした。ページを開き直してもう一度お試しください。";
  }

  return toGenericErrorMessage();
}

function getCompletionErrorMessage(errorCode: string | null) {
  switch (errorCode) {
    case "access_denied":
      return "LINEログインがキャンセルされました。";
    case "not_linked":
      return "LINE公式アカウントの友だち追加まで完了してから、もう一度お試しください。";
    case "callback_failed":
      return "LINE連携の確認に失敗しました。もう一度お試しください。";
    default:
      return "LINE連携を完了できませんでした。";
  }
}

export function LineLinkCard({ returnTo, returnState }: LineLinkCardProps) {
  const popupRef = useRef<Window | null>(null);
  const [lineLinkSnapshot, setLineLinkSnapshot] = useState<LineLinkSnapshot>(readLineLinkSnapshot());
  const [statusLoading, setStatusLoading] = useState(false);
  const [connectSubmitting, setConnectSubmitting] = useState(false);
  const [disconnectSubmitting, setDisconnectSubmitting] = useState(false);
  const [disconnectConfirmOpen, setDisconnectConfirmOpen] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [connectSuccess, setConnectSuccess] = useState<string | null>(null);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);
  const [disconnectSuccess, setDisconnectSuccess] = useState<string | null>(null);

  const statusLabel = useMemo(() => getLineLinkStatusLabel(lineLinkSnapshot), [lineLinkSnapshot]);
  const statusDescription = useMemo(
    () => getLineLinkStatusDescription(lineLinkSnapshot),
    [lineLinkSnapshot],
  );
  const linkedAtLabel = useMemo(() => formatLinkedAt(lineLinkSnapshot.linkedAt), [lineLinkSnapshot.linkedAt]);
  const isLinked = lineLinkSnapshot.status === "linked";

  const applyLineStatus = useCallback((status: LineLinkStatusData | LineLinkDisconnectData) => {
    const nextSnapshot = resolveLineLinkSnapshotFromApiStatus(status, readLineLinkSnapshot());
    setLineLinkSnapshot(nextSnapshot);
    writeLineLinkSnapshot(nextSnapshot);
    return nextSnapshot;
  }, []);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    setStatusError(null);

    try {
      const status = await api.getLineLinkStatus();
      applyLineStatus(status);
    } catch (error) {
      logError(error);
      setStatusError("LINE連携状態の確認に失敗しました。");
      setLineLinkSnapshot(readLineLinkSnapshot());
    } finally {
      setStatusLoading(false);
    }
  }, [applyLineStatus]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const consumeCompletion = useCallback(
    async (errorCode: string | null, lineConnect: "success" | "error") => {
      clearLineOAuthPopupSnapshot();
      setConnectSubmitting(false);

      if (popupRef.current && popupRef.current.closed === false) {
        try {
          popupRef.current.close();
        } catch {
          // noop
        }
      }
      popupRef.current = null;

      if (lineConnect === "error") {
        setConnectError(getCompletionErrorMessage(errorCode));
        return;
      }

      try {
        const status = await api.getLineLinkStatus();
        const nextSnapshot = applyLineStatus(status);
        if (nextSnapshot.status !== "linked") {
          setConnectError(getCompletionErrorMessage("not_linked"));
          return;
        }

        setConnectError(null);
        setConnectSuccess("LINE連携が完了しました。");
      } catch (error) {
        logError(error);
        setConnectError(getCompletionErrorMessage("callback_failed"));
      }
    },
    [applyLineStatus],
  );

  useEffect(() => {
    const consumeStoredSnapshot = async () => {
      const snapshot = readLineOAuthPopupSnapshot();
      if (!snapshot) return;
      await consumeCompletion(snapshot.errorCode, snapshot.lineConnect);
    };

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (!isLineOAuthPopupMessage(event.data)) return;
      void consumeCompletion(event.data.errorCode, event.data.lineConnect);
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== LINE_OAUTH_STORAGE_KEY || !event.newValue) return;
      void consumeStoredSnapshot();
    };

    const handleFocus = () => {
      void consumeStoredSnapshot();
    };

    window.addEventListener("message", handleMessage);
    window.addEventListener("storage", handleStorage);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("pageshow", handleFocus);

    return () => {
      window.removeEventListener("message", handleMessage);
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("pageshow", handleFocus);
    };
  }, [consumeCompletion]);

  const handleConnect = async () => {
    setConnectSubmitting(true);
    setConnectError(null);
    setConnectSuccess(null);
    setDisconnectError(null);
    setDisconnectSuccess(null);

    try {
      writeLineOAuthContextSnapshot({ returnTo, returnState });

      const response: LineOAuthAuthorizeData = await api.startLineOAuth({
        redirectUri: resolveLineOAuthPopupCompleteUrl(),
      });

      if (isMobileUserAgent()) {
        window.location.href = response.authorizeUrl;
        return;
      }

      const popup = openCenteredPopup(response.authorizeUrl);
      if (!popup) {
        throw new AppError("LINE popup was blocked.", "INVALID_RESPONSE");
      }

      popupRef.current = popup;
    } catch (error) {
      logError(error);
      setConnectSubmitting(false);
      setConnectError(getConnectErrorMessage(error));
    }
  };

  const handleDisconnect = async () => {
    setDisconnectSubmitting(true);
    setDisconnectError(null);
    setDisconnectSuccess(null);
    setConnectError(null);
    setConnectSuccess(null);

    try {
      const response = await api.disconnectLineLink();
      applyLineStatus(response);
      setDisconnectConfirmOpen(false);
      setDisconnectSuccess("LINE連携を解除しました。");
    } catch (error) {
      logError(error);
      setDisconnectError(getDisconnectErrorMessage(error));
    } finally {
      setDisconnectSubmitting(false);
    }
  };

  return (
    <>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <p className={`max-w-md text-sm leading-6 ${UI_TEXT_TONE.body}`}>
          LINEログインで認証し、必要に応じて公式アカウントの友だち追加までまとめて進めます。
        </p>

        <div className="flex flex-col gap-2 sm:items-end">
          {!isLinked ? (
            <button
              type="button"
              onClick={handleConnect}
              disabled={connectSubmitting || disconnectSubmitting}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-full bg-[#5F8B57] px-5 py-3 text-sm font-black text-white transition hover:bg-[#4E7348] disabled:cursor-not-allowed disabled:opacity-70"
            >
              {connectSubmitting ? "LINEへ移動中..." : "LINE連携を開始する"}
            </button>
          ) : null}

          {isLinked ? (
            <button
              type="button"
              onClick={() => setDisconnectConfirmOpen(true)}
              disabled={statusLoading || disconnectSubmitting}
              className="inline-flex items-center justify-center rounded-full border border-[#C8D8C2] bg-white px-4 py-2 text-xs font-bold text-[#4A604B] transition hover:bg-[#F8FCF5] disabled:cursor-not-allowed disabled:opacity-70"
            >
              {disconnectSubmitting ? "解除中..." : "連携を解除"}
            </button>
          ) : null}
        </div>
      </div>

      <div className="mt-4 rounded-[1.8rem] border border-[#D8E7D3] bg-[#F7FBF3] px-5 py-5 shadow-[0_10px_24px_rgba(95,139,87,0.08)]">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className={`text-[12px] font-bold tracking-[0.14em] ${UI_TEXT_TONE.eyebrow}`}>現在の連携状態</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span
                className={`rounded-full border px-3 py-1 text-xs font-black ${getLineStatusBadgeClass(lineLinkSnapshot)}`}
              >
                {statusLoading ? "確認中..." : statusLabel}
              </span>
              {lineLinkSnapshot.lineDisplayName ? (
                <span className="text-xs font-bold text-[#6F8B63]">{lineLinkSnapshot.lineDisplayName}</span>
              ) : null}
            </div>
            <p className={`mt-3 text-sm leading-6 ${UI_TEXT_TONE.sectionTitle}`}>{statusDescription}</p>
            {linkedAtLabel ? (
              <p className={`mt-2 text-xs ${UI_TEXT_TONE.meta}`}>連携日時: {linkedAtLabel}</p>
            ) : null}
          </div>

          <button
            type="button"
            onClick={() => void loadStatus()}
            disabled={statusLoading || connectSubmitting || disconnectSubmitting}
            className="inline-flex items-center justify-center rounded-full border border-[#C8D8C2] bg-white px-4 py-2.5 text-sm font-bold text-[#4A604B] transition hover:bg-[#F8FCF5] disabled:cursor-not-allowed disabled:opacity-70"
          >
            {statusLoading ? "確認中..." : "状態を更新"}
          </button>
        </div>
      </div>

      <div className={`mt-4 rounded-[1.5rem] border border-white/70 bg-white/80 p-4 text-sm leading-6 ${UI_TEXT_TONE.sectionTitle}`}>
        <p>1. 「LINE連携を開始する」からLINEの認証画面を開きます。</p>
        <p>2. 認証後、そのまま公式アカウントの友だち追加まで進めます。</p>
        <p>3. 完了するとこの画面に戻り、連携状態が更新されます。</p>
      </div>

      {statusError ? (
        <div className="mt-4 rounded-[1.4rem] border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm text-[#A1493E]">
          {statusError}
        </div>
      ) : null}
      {connectSuccess ? (
        <div className="mt-4 rounded-[1.4rem] border border-[#D8E7D3] bg-[#F7FBF3] px-4 py-3 text-sm text-[#58705A]">
          {connectSuccess}
        </div>
      ) : null}
      {connectError ? (
        <div className="mt-4 rounded-[1.4rem] border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm text-[#A1493E]">
          {connectError}
        </div>
      ) : null}
      {disconnectSuccess ? (
        <div className="mt-4 rounded-[1.4rem] border border-[#D8E7D3] bg-[#F7FBF3] px-4 py-3 text-sm text-[#58705A]">
          {disconnectSuccess}
        </div>
      ) : null}
      {disconnectError ? (
        <div className="mt-4 rounded-[1.4rem] border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm text-[#A1493E]">
          {disconnectError}
        </div>
      ) : null}

      {disconnectConfirmOpen ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center overflow-x-hidden bg-[#2B211D]/45 px-4 sm:px-6">
          <div className="w-full max-w-sm rounded-[2rem] border border-[#D8E7D3] bg-white p-6 shadow-[0_24px_60px_rgba(62,85,64,0.18)]">
            <p className={`text-[12px] font-black uppercase tracking-[0.18em] ${UI_TEXT_TONE.eyebrow}`}>確認</p>
            <h3 className={`mt-2 text-xl font-black ${UI_TEXT_TONE.sectionTitle}`}>LINE連携を解除しますか？</h3>
            <p className={`mt-3 text-sm leading-6 ${UI_TEXT_TONE.bodyMuted}`}>
              解除すると、LINEでの通知や配信は使えなくなります。再利用するには、あらためてLINE連携が必要です。
            </p>
            <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => setDisconnectConfirmOpen(false)}
                disabled={disconnectSubmitting}
                className="flex-1 rounded-full border border-[#C8D8C2] bg-white px-4 py-3 text-sm font-bold text-[#6A4F44] transition hover:bg-[#F8FCF5] disabled:cursor-not-allowed disabled:opacity-70"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={handleDisconnect}
                disabled={disconnectSubmitting}
                className="flex-1 rounded-full bg-[#A1493E] px-4 py-3 text-sm font-black text-white transition hover:bg-[#8E3E35] disabled:cursor-not-allowed disabled:opacity-70"
              >
                {disconnectSubmitting ? "解除中..." : "解除する"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
